/**
 * PreflightChecklist — Internal pre-flight checklist for the Work tab.
 *
 * Displays a project's pre-flight items grouped by category with:
 *   - Readiness progress bar
 *   - Checkbox toggle for binary/threshold fulfillment
 *   - Modal add/edit form
 *   - Category grouping with collapsible headers
 *   - Visibility badges (internal vs public)
 *   - Mission metric link indicators
 *
 * Progressive & opt-in: if a project has zero items, shows a friendly
 * empty state with an "Add Item" button. Zero overhead by default.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type {
  PreflightItem,
  ResourceCategory,
  PreflightRequirementType,
  PreflightVisibility,
  PreflightSummary,
  PreflightTemplate,
  PreflightState,
  PreflightPriority,
  DescriptorTag,
  ResourcePhase,
} from '../../Data/PreFlight/preflightTypes';
import {
  RESOURCE_CATEGORIES,
  PREFLIGHT_REQUIREMENT_TYPES,
  PREFLIGHT_STATES,
  PREFLIGHT_PRIORITIES,
  DESCRIPTOR_TAGS,
  RESOURCE_PHASES,
  STATE_TRANSITIONS,
  normalizeCategory,
  normalizePhase,
  computePreflightSummary,
  computeAutoState,
  getBlockingItems,
  hasCircularDependency,
  isValidTransition,
} from '../../Data/PreFlight/preflightTypes';
import TagChip from '../TagChip/TagChip';
import { preflightTemplateSeedData } from '../../Data/PreFlight/preflightTemplateSeedData';
import { applyPreflightTemplate } from '../../Data/PreFlight/applyTemplate';
import { openBomPrintWindow } from '../../Data/PreFlight/generateBomHtml';
import { fetchGanttTasksByProject, apiResponseToGanttTask } from '../../api/ganttTasks';
import type { GanttTask } from '../../api/ganttTasks';
import ResourceImportModal from './ResourceImportModal';
import styles from './PreflightChecklist.module.css';

/* ------------------------------------------------------------------ */
/*  Category icons (inline SVG for zero-dep rendering)                 */
/* ------------------------------------------------------------------ */

const CATEGORY_ICONS: Record<string, string> = {
  materials:       '\u{1F4E6}', // 📦
  equipment:       '\u{1F527}', // 🔧
  services:        '\u{1F91D}', // 🤝
  labor:           '\u{1F477}', // 👷
  transportation:  '\u{1F68C}', // 🚌
  facilities:      '\u{1F3E2}', // 🏢
  admin_legal:     '\u{1F4CB}', // 📋
  knowledge:       '\u{1F4DA}', // 📚
  financial:       '\u{1F4B0}', // 💰
  in_kind:         '\u{1F381}', // 🎁
  software_tech:   '\u{1F4BB}', // 💻
  other:           '\u{1F4CE}', // 📎
  // Legacy keys for items that haven't been migrated yet
  tools_equipment: '\u{1F527}', // 🔧
  administrative:  '\u{1F4CB}', // 📋
  land_site:       '\u{1F30D}', // 🌍
};

/* ------------------------------------------------------------------ */
/*  Category card gradient backgrounds (dark/light safe via rgba)      */
/* ------------------------------------------------------------------ */

const CATEGORY_GRADIENTS: Record<string, string> = {
  materials:       'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(245,158,11,0.16) 100%)',
  equipment:       'linear-gradient(135deg, rgba(107,114,128,0.08) 0%, rgba(107,114,128,0.16) 100%)',
  services:        'linear-gradient(135deg, rgba(6,182,212,0.08) 0%, rgba(6,182,212,0.16) 100%)',
  labor:           'linear-gradient(135deg, rgba(234,88,12,0.08) 0%, rgba(234,88,12,0.16) 100%)',
  transportation:  'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(99,102,241,0.16) 100%)',
  facilities:      'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(16,185,129,0.16) 100%)',
  admin_legal:     'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(59,130,246,0.16) 100%)',
  knowledge:       'linear-gradient(135deg, rgba(168,85,247,0.08) 0%, rgba(168,85,247,0.16) 100%)',
  financial:       'linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.16) 100%)',
  in_kind:         'linear-gradient(135deg, rgba(236,72,153,0.08) 0%, rgba(236,72,153,0.16) 100%)',
  software_tech:   'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(139,92,246,0.16) 100%)',
  other:           'linear-gradient(135deg, rgba(148,163,184,0.08) 0%, rgba(148,163,184,0.16) 100%)',
  // Legacy
  tools_equipment: 'linear-gradient(135deg, rgba(107,114,128,0.08) 0%, rgba(107,114,128,0.16) 100%)',
  administrative:  'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(59,130,246,0.16) 100%)',
  land_site:       'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(16,185,129,0.16) 100%)',
};

/* ------------------------------------------------------------------ */
/*  Phase icons                                                        */
/* ------------------------------------------------------------------ */

const PHASE_ICONS: Record<ResourcePhase, string> = {
  preparation: '\u{1F4CB}',   // 📋
  operational: '\u{2699}\uFE0F', // ⚙️
  sustainment: '\u{1F331}',   // 🌱
};

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface PreflightChecklistProps {
  projectGuid: string;
  /** Project display name — used in BOM print header. */
  projectName?: string;
  /** ISO 4217 currency code for BOM print. Defaults to 'USD'. */
  budgetCurrency?: string;
  items: PreflightItem[];
  /** Callback when items change (add/edit/delete/toggle). */
  onItemsChange: (updatedItems: PreflightItem[]) => void;
  /** Whether the current user can edit. */
  canEdit?: boolean;
  debugMode?: boolean;
  /** When true, hides the built-in summary bar (readiness progress, print, view toggle).
   *  Use when the parent (ResourcesWorkspace) provides its own dashboard + toolbar. */
  hideSummaryBar?: boolean;
  /** Externally controlled view mode. When provided, overrides internal state. */
  externalViewMode?: 'cards' | 'list';
}

/* ------------------------------------------------------------------ */
/*  UUID helper — backend expects proper Guid values                   */
/* ------------------------------------------------------------------ */

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto?.randomUUID) return crypto.randomUUID().toUpperCase();
  // Fallback for older environments: RFC 4122 v4 UUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/* ------------------------------------------------------------------ */
/*  Blank item factory                                                 */
/* ------------------------------------------------------------------ */

function makeBlankItem(projectGuid: string, sortOrder: number, phase: ResourcePhase = 'preparation'): PreflightItem {
  const now = new Date().toISOString();
  return {
    item_guid: generateUUID(),
    project_guid: projectGuid,
    phase,
    category: 'materials',
    label: '',
    description: '',
    requirement_type: 'binary',
    target_quantity: null,
    unit: null,
    current_quantity: 0,
    is_fulfilled: false,
    state: 'seeking',
    priority: 'important',
    descriptor_tags: [],
    matching_conditions: null,
    linked_offer_guid: null,
    withdrawn_at: null,
    visibility: 'internal',
    mission_subkpi_key: null,
    tags: [],
    sort_order: sortOrder,
    created_by: '',
    created_at: now,
    updated_at: now,
    // Cost / budget fields
    cost_category: 'materials',
    unit_cost: null,
    actual_cost: null,
    cost_currency: null,
    cost_note: null,
    // EVM-Lite fields
    gantt_task_id: null,
    percent_complete: 0,
    // Fulfillment / Acquisition fields (DEV-647)
    fulfillment_note: null,
    assignee_guid: null,
  };
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function PreflightChecklist({
  projectGuid,
  projectName = 'Project',
  budgetCurrency,
  items,
  onItemsChange,
  canEdit = true,
  debugMode,
  hideSummaryBar = false,
  externalViewMode,
}: PreflightChecklistProps) {
  const { t } = useAppTranslation('kpi');

  /* ── View mode ──────────────────────────────────────────────── */
  const [internalViewMode, setInternalViewMode] = useState<'cards' | 'list'>('cards');
  const viewMode = externalViewMode ?? internalViewMode;
  const setViewMode = externalViewMode ? (() => {}) : setInternalViewMode;

  /* ── Edit state ──────────────────────────────────────────────── */
  const [editingItem, setEditingItem] = useState<PreflightItem | null>(null);
  const [isNewItem, setIsNewItem] = useState(false);
  const [collapsedCats, setCollapsedCats] = useState<Set<ResourceCategory>>(new Set());
  const [collapsedPhases, setCollapsedPhases] = useState<Set<ResourcePhase>>(new Set());
  const [expandedItemGuid, setExpandedItemGuid] = useState<string | null>(null);
  /** Which category card is expanded (card view accordion). Key = "phase:category". */
  const [expandedCardKey, setExpandedCardKey] = useState<string | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [templateNotice, setTemplateNotice] = useState<string | null>(null);

  /* ── Multi-select state ────────────────────────────────────── */
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedGuids, setSelectedGuids] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /* ── Import undo state ─────────────────────────────────────── */
  const [lastImportGuids, setLastImportGuids] = useState<string[] | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Save error state (shown when batch + fallback both fail) ── */
  const [saveError, setSaveError] = useState<string | null>(null);

  /* ── Fulfillment note state (DEV-647) ─────────────────────── */
  /** GUID of the item currently showing the inline fulfillment note input (after met transition). */
  const [pendingNoteGuid, setPendingNoteGuid] = useState<string | null>(null);
  const [pendingNoteText, setPendingNoteText] = useState('');
  const fulfillmentNoteRef = useRef<HTMLInputElement>(null);

  // Auto-focus the fulfillment note input when it appears
  useEffect(() => {
    if (pendingNoteGuid && fulfillmentNoteRef.current) {
      // Small delay to allow DOM to settle after state transition
      const timer = setTimeout(() => fulfillmentNoteRef.current?.focus(), 80);
      return () => clearTimeout(timer);
    }
  }, [pendingNoteGuid]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.failCount) {
        setSaveError(
          t('preflight.saveError', {
            failCount: String(detail.failCount),
            totalCount: String(detail.totalCount),
          })
        );
        setTimeout(() => setSaveError(null), 8000);
      }
    };
    window.addEventListener('l4e_preflight_save_error', handler);
    return () => window.removeEventListener('l4e_preflight_save_error', handler);
  }, [t]);

  /* ── Gantt tasks for Schedule Link dropdown (lazy loaded) ──── */
  const [ganttTasks, setGanttTasks] = useState<GanttTask[]>([]);
  const ganttLoadedForProject = useRef<string | null>(null);

  useEffect(() => {
    if (!editingItem || !projectGuid) return;
    if (ganttLoadedForProject.current === projectGuid) return;
    ganttLoadedForProject.current = projectGuid;
    fetchGanttTasksByProject(projectGuid)
      .then((resp) => setGanttTasks(resp.map(apiResponseToGanttTask)))
      .catch(() => setGanttTasks([]));
  }, [editingItem, projectGuid]);

  /* ── Available templates ────────────────────────────────────── */
  const templates = useMemo(() => preflightTemplateSeedData, []);

  /* ── Derived ─────────────────────────────────────────────────── */
  const summary: PreflightSummary = useMemo(() => computePreflightSummary(items), [items]);

  const grouped = useMemo(() => {
    const map = new Map<ResourceCategory, PreflightItem[]>();
    for (const cat of RESOURCE_CATEGORIES) {
      const catItems = items.filter((i) => i.category === cat);
      if (catItems.length > 0) map.set(cat, catItems);
    }
    return map;
  }, [items]);

  /** Items grouped by phase → category for the phase-section layout. */
  const groupedByPhase = useMemo(() => {
    const map = new Map<ResourcePhase, Map<ResourceCategory, PreflightItem[]>>();
    for (const phase of RESOURCE_PHASES) {
      const phaseItems = items.filter((i) => normalizePhase(i.phase) === phase);
      const catMap = new Map<ResourceCategory, PreflightItem[]>();
      for (const cat of RESOURCE_CATEGORIES) {
        const catItems = phaseItems.filter((i) => i.category === cat);
        if (catItems.length > 0) catMap.set(cat, catItems);
      }
      if (catMap.size > 0) map.set(phase, catMap);
    }
    return map;
  }, [items]);

  /* ── Handlers ────────────────────────────────────────────────── */

  const expandCategory = useCallback((cat: ResourceCategory) => {
    setCollapsedCats((prev) => {
      if (!prev.has(cat)) return prev;
      const next = new Set(prev);
      next.delete(cat);
      return next;
    });
  }, []);

  const collapseCategory = useCallback((cat: ResourceCategory) => {
    setCollapsedCats((prev) => {
      if (prev.has(cat)) return prev;
      const next = new Set(prev);
      next.add(cat);
      return next;
    });
  }, []);

  const expandPhase = useCallback((phase: ResourcePhase) => {
    setCollapsedPhases((prev) => {
      if (!prev.has(phase)) return prev;
      const next = new Set(prev);
      next.delete(phase);
      return next;
    });
  }, []);

  const collapsePhase = useCallback((phase: ResourcePhase) => {
    setCollapsedPhases((prev) => {
      if (prev.has(phase)) return prev;
      const next = new Set(prev);
      next.add(phase);
      return next;
    });
  }, []);

  /**
   * Handle state transition for a pre-flight item.
   * Replaces the old handleToggleFulfilled checkbox toggle.
   * Derives is_fulfilled from state === 'met' for backward compat.
   */
  const handleStateTransition = useCallback(
    (item: PreflightItem, newState: PreflightState) => {
      if (!canEdit) return;
      if (!isValidTransition(item.state, newState)) return;
      const updated = items.map((i) => {
        if (i.item_guid.toUpperCase() !== item.item_guid.toUpperCase()) return i;
        const isMet = newState === 'met';
        const patched: PreflightItem = {
          ...i,
          state: newState,
          is_fulfilled: isMet,
          current_quantity: i.requirement_type === 'binary'
            ? (isMet ? 1 : 0)
            : (isMet ? (i.target_quantity ?? 1) : i.current_quantity),
          withdrawn_at: newState === 'withdrawn' ? new Date().toISOString() : null,
          updated_at: new Date().toISOString(),
        };
        return patched;
      });
      // After the transition, auto-manage blocked states for dependent items
      const autoFixed = updated.map((i) => {
        const autoState = computeAutoState(i, updated);
        if (autoState !== i.state) {
          return { ...i, state: autoState, is_fulfilled: autoState === 'met', updated_at: new Date().toISOString() };
        }
        return i;
      });
      onItemsChange(autoFixed);
    },
    [items, onItemsChange, canEdit]
  );

  /** Toggle visibility between 'internal' and 'public'. */
  const handleVisibilityToggle = useCallback(
    (item: PreflightItem, newVisibility: PreflightVisibility) => {
      if (!canEdit) return;
      if (item.visibility === newVisibility) return;
      const updated = items.map((i) => {
        if (i.item_guid.toUpperCase() !== item.item_guid.toUpperCase()) return i;
        return { ...i, visibility: newVisibility, updated_at: new Date().toISOString() };
      });
      onItemsChange(updated);
    },
    [items, onItemsChange, canEdit]
  );

  /** Convenience: one-click "Mark Done" (seeking/partial → met).
   *  When transitioning TO met, opens the inline fulfillment note input (DEV-647). */
  const handleToggleFulfilled = useCallback(
    (item: PreflightItem) => {
      if (!canEdit) return;
      const newState: PreflightState = item.state === 'met' ? 'seeking' : 'met';
      handleStateTransition(item, newState);
      // Show inline note input when transitioning TO met
      if (newState === 'met') {
        setPendingNoteGuid(item.item_guid);
        setPendingNoteText('');
      } else {
        // Clearing met → dismiss any pending note
        if (pendingNoteGuid === item.item_guid) {
          setPendingNoteGuid(null);
          setPendingNoteText('');
        }
      }
    },
    [canEdit, handleStateTransition, pendingNoteGuid]
  );

  /** Save a fulfillment note and dismiss the inline input (DEV-647). */
  const handleSaveFulfillmentNote = useCallback(
    (itemGuid: string) => {
      if (pendingNoteText.trim()) {
        const updated = items.map((i) => {
          if (i.item_guid.toUpperCase() !== itemGuid.toUpperCase()) return i;
          const existingNote = i.fulfillment_note || '';
          const newNote = existingNote
            ? existingNote + '\n' + pendingNoteText.trim()
            : pendingNoteText.trim();
          return { ...i, fulfillment_note: newNote, updated_at: new Date().toISOString() };
        });
        onItemsChange(updated);
      }
      setPendingNoteGuid(null);
      setPendingNoteText('');
    },
    [items, onItemsChange, pendingNoteText]
  );

  /** Dismiss the fulfillment note input without saving. */
  const handleDismissFulfillmentNote = useCallback(() => {
    setPendingNoteGuid(null);
    setPendingNoteText('');
  }, []);

  const handleAddItem = useCallback(() => {
    const nextOrder = items.length > 0 ? Math.max(...items.map((i) => i.sort_order)) + 1 : 1;
    setEditingItem(makeBlankItem(projectGuid, nextOrder));
    setIsNewItem(true);
  }, [items, projectGuid]);

  const handleEditItem = useCallback((item: PreflightItem) => {
    setEditingItem({ ...item });
    setIsNewItem(false);
  }, []);

  const handleDeleteItem = useCallback(
    (item: PreflightItem) => {
      if (!canEdit) return;
      const updated = items.filter((i) => i.item_guid.toUpperCase() !== item.item_guid.toUpperCase());
      onItemsChange(updated);
    },
    [items, onItemsChange, canEdit]
  );

  const handleSaveEdit = useCallback(() => {
    if (!editingItem || !editingItem.label.trim()) return;
    const now = new Date().toISOString();
    const saved = {
      ...editingItem,
      label: editingItem.label.trim(),
      updated_at: now,
      // Clear stale API-computed totals so BOM rollup always recomputes
      // from the fresh unit_cost / actual_cost / quantity fields.
      estimated_total: null,
      actual_total: null,
    };

    let updated: PreflightItem[];
    if (isNewItem) {
      updated = [...items, saved];
    } else {
      updated = items.map((i) => (i.item_guid.toUpperCase() === saved.item_guid.toUpperCase() ? saved : i));
    }
    onItemsChange(updated);
    setEditingItem(null);
    setIsNewItem(false);
  }, [editingItem, isNewItem, items, onItemsChange]);

  const handleCancelEdit = useCallback(() => {
    setEditingItem(null);
    setIsNewItem(false);
  }, []);

  const handleApplyTemplate = useCallback(
    (templateId: string) => {
      const template = templates.find((tp) => tp.template_guid === templateId);
      if (!template) return;
      const nextOrder = items.length > 0 ? Math.max(...items.map((i) => i.sort_order)) + 1 : 1;
      const newItems = applyPreflightTemplate(template, projectGuid, '', nextOrder);
      onItemsChange([...items, ...newItems]);
      setShowTemplatePicker(false);
      setTemplateNotice(
        t('preflight.templateApplied', { count: String(newItems.length) })
      );
      setTimeout(() => setTemplateNotice(null), 4000);
    },
    [templates, items, projectGuid, onItemsChange, t]
  );

  const handleImport = useCallback(
    (importedItems: PreflightItem[]) => {
      const guids = importedItems.map((i) => i.item_guid);
      onItemsChange([...items, ...importedItems]);
      setShowImportModal(false);

      // Track for undo — stays visible until user dismisses, undoes, or imports again
      setLastImportGuids(guids);

      setTemplateNotice(
        t('preflight.importSuccess', { count: String(importedItems.length) })
      );
      setTimeout(() => setTemplateNotice(null), 4000);
    },
    [items, onItemsChange, t]
  );

  /** Undo last import — removes all items from the last import batch. */
  const handleUndoImport = useCallback(() => {
    if (!lastImportGuids) return;
    const guidSet = new Set(lastImportGuids);
    onItemsChange(items.filter((i) => !guidSet.has(i.item_guid)));
    setLastImportGuids(null);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setTemplateNotice(t('preflight.importUndone'));
    setTimeout(() => setTemplateNotice(null), 3000);
  }, [lastImportGuids, items, onItemsChange, t]);

  /* ── Multi-select handlers ─────────────────────────────────── */

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => {
      if (prev) setSelectedGuids(new Set());
      return !prev;
    });
    setShowDeleteConfirm(false);
  }, []);

  const toggleSelectItem = useCallback((guid: string) => {
    setSelectedGuids((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    const activeGuids = items.filter((i) => i.state !== 'withdrawn').map((i) => i.item_guid);
    setSelectedGuids(new Set(activeGuids));
  }, [items]);

  const deselectAll = useCallback(() => {
    setSelectedGuids(new Set());
  }, []);

  const handleBulkDelete = useCallback(() => {
    if (selectedGuids.size === 0) return;
    onItemsChange(items.filter((i) => !selectedGuids.has(i.item_guid)));
    setSelectedGuids(new Set());
    setSelectionMode(false);
    setShowDeleteConfirm(false);
    setTemplateNotice(
      t('preflight.bulkDeleted', { count: String(selectedGuids.size) })
    );
    setTimeout(() => setTemplateNotice(null), 3000);
  }, [selectedGuids, items, onItemsChange, t]);

  /* ── Card click → expand card in-place to show items ────────── */
  const handleCardClick = useCallback((phase: ResourcePhase, cat: ResourceCategory) => {
    const key = `${phase}:${cat}`;
    setExpandedCardKey((prev) => (prev === key ? null : key));
  }, []);

  /* ── Empty state ─────────────────────────────────────────────── */

  if (items.length === 0 && !editingItem) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
          <h4 className={styles.emptyTitle}>{t('preflight.noItems')}</h4>
          <p className={styles.emptyHint}>{t('preflight.noItemsHint')}</p>
          {canEdit && (
            <div className={styles.emptyActions}>
              <button type="button" className={styles.addBtn} onClick={handleAddItem}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                {t('preflight.addItem')}
              </button>
              {templates.length > 0 && (
                <button
                  type="button"
                  className={styles.templateBtn}
                  onClick={() => setShowTemplatePicker((prev) => !prev)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <line x1="3" y1="9" x2="21" y2="9" />
                    <line x1="9" y1="21" x2="9" y2="9" />
                  </svg>
                  {t('preflight.applyTemplate')}
                </button>
              )}
              <button
                type="button"
                className={styles.templateBtn}
                onClick={() => setShowImportModal(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
                {t('preflight.importFromExcel')}
              </button>
            </div>
          )}
          {showTemplatePicker && (
            <TemplatePicker
              templates={templates}
              t={t}
              onApply={handleApplyTemplate}
              onClose={() => setShowTemplatePicker(false)}
            />
          )}

          {showImportModal && (
            <ResourceImportModal
              projectGuid={projectGuid}
              existingCount={1}
              onImport={handleImport}
              onCancel={() => setShowImportModal(false)}
            />
          )}
        </div>
      </div>
    );
  }

  /* ── Main render ─────────────────────────────────────────────── */

  return (
    <div className={styles.container}>
      {/* ── Readiness summary bar + view toggle (hidden when parent provides its own) ── */}
      {items.length > 0 && !hideSummaryBar && (
        <div className={styles.summaryBar}>
          <span className={styles.summaryLabel}>{t('preflight.readiness')}</span>
          <div className={styles.progressBarOuter}>
            <div
              className={`${styles.progressBarInner} ${
                summary.readiness_pct === 100 ? styles.progressComplete : styles.progressPartial
              }`}
              style={{ width: `${summary.readiness_pct}%` }}
            />
          </div>
          <span className={styles.summaryText}>
            {summary.readiness_pct === 100
              ? t('preflight.allFulfilled')
              : t('preflight.fulfilled', {
                  count: String(summary.fulfilled_items),
                  total: String(summary.total_items),
                })}
          </span>

          {/* Print BOM */}
          <button
            type="button"
            className={styles.viewBtn}
            onClick={() => openBomPrintWindow(items, projectName, budgetCurrency)}
            aria-label={t('preflight.bomPrint')}
            title={t('preflight.bomPrint')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
          </button>

          {/* View toggle */}
          <div className={styles.viewToggle}>
            <button
              type="button"
              className={viewMode === 'cards' ? styles.viewBtnActive : styles.viewBtn}
              onClick={() => setViewMode('cards')}
              aria-label={t('preflight.viewCards')}
              title={t('preflight.viewCards')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            </button>
            <button
              type="button"
              className={viewMode === 'list' ? styles.viewBtnActive : styles.viewBtn}
              onClick={() => { setViewMode('list'); setCollapsedCats(new Set()); }}
              aria-label={t('preflight.viewList')}
              title={t('preflight.viewList')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Import undo toast ──────────────────────────────────── */}
      {lastImportGuids && (
        <div className={styles.undoToast}>
          <span>
            {t('preflight.importSuccess', { count: String(lastImportGuids.length) })}
          </span>
          <button type="button" className={styles.undoBtn} onClick={handleUndoImport}>
            {t('preflight.undoImport')}
          </button>
          <button
            type="button"
            className={styles.undoDismiss}
            onClick={() => { setLastImportGuids(null); if (undoTimerRef.current) clearTimeout(undoTimerRef.current); }}
            aria-label="Dismiss"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Save error toast ─────────────────────────────────────── */}
      {saveError && (
        <div className={styles.saveErrorToast}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{saveError}</span>
          <button
            type="button"
            className={styles.undoDismiss}
            onClick={() => setSaveError(null)}
            aria-label="Dismiss"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Selection toolbar ────────────────────────────────────── */}
      {canEdit && items.length > 0 && (
        <div className={styles.selectionBar}>
          <button type="button" className={styles.selectModeBtn} onClick={toggleSelectionMode}>
            {selectionMode ? t('preflight.cancelSelection') : t('preflight.selectItems')}
          </button>
          {selectionMode && (
            <>
              <button
                type="button"
                className={styles.selectAllBtn}
                onClick={selectedGuids.size === items.filter((i) => i.state !== 'withdrawn').length ? deselectAll : selectAll}
              >
                {selectedGuids.size === items.filter((i) => i.state !== 'withdrawn').length
                  ? t('preflight.deselectAll')
                  : t('preflight.selectAll')}
              </button>
              <span className={styles.selectCount}>
                {t('preflight.selectedCount', { count: String(selectedGuids.size) })}
              </span>
              {selectedGuids.size > 0 && !showDeleteConfirm && (
                <button
                  type="button"
                  className={styles.bulkDeleteBtn}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  {t('preflight.deleteSelected')}
                </button>
              )}
              {showDeleteConfirm && (
                <div className={styles.deleteConfirmInline}>
                  <span className={styles.deleteConfirmText}>
                    {t('preflight.confirmBulkDelete', { count: String(selectedGuids.size) })}
                  </span>
                  <button type="button" className={styles.deleteConfirmYes} onClick={handleBulkDelete}>
                    {t('preflight.confirmYes')}
                  </button>
                  <button type="button" className={styles.deleteConfirmNo} onClick={() => setShowDeleteConfirm(false)}>
                    {t('preflight.cancelEdit')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Phase Sections ──────────────────────────────────────── */}
      {items.length > 0 && RESOURCE_PHASES.map((phase) => {
        const phaseCatMap = groupedByPhase.get(phase);
        if (!phaseCatMap || phaseCatMap.size === 0) return null;

        const phaseCollapsed = collapsedPhases.has(phase);
        const phaseSummary = summary.by_phase[phase];
        const phasePct = phaseSummary.total > 0
          ? Math.round((phaseSummary.fulfilled / phaseSummary.total) * 100)
          : 0;

        return (
          <div key={phase} className={styles.phaseSection}>
            {/* Phase header — flat divider style */}
            <div
              className={styles.phaseHeader}
              onClick={() => collapsedPhases.has(phase) ? expandPhase(phase) : collapsePhase(phase)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') collapsedPhases.has(phase) ? expandPhase(phase) : collapsePhase(phase); }}
            >
              <span className={styles.phaseIcon}>{PHASE_ICONS[phase]}</span>
              <span className={styles.phaseName}>{t(`preflight.phases.${phase}`)}</span>
              <div className={styles.phaseDividerLine} />
              <div className={styles.phaseReadiness}>
                <div className={styles.phaseBar}>
                  <div
                    className={`${styles.phaseBarFill} ${phasePct === 100 ? styles.phaseBarFillComplete : styles.phaseBarFillPartial}`}
                    style={{ width: `${phasePct}%` }}
                  />
                </div>
                <span className={styles.phaseBarText}>
                  {t('preflight.ofTotal', {
                    fulfilled: String(phaseSummary.fulfilled),
                    total: String(phaseSummary.total),
                  })}
                </span>
              </div>
              <svg
                width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke="var(--text-muted)" strokeWidth="2"
                style={{ transform: phaseCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            {/* Phase content — card or list view */}
            {!phaseCollapsed && (
              <div className={styles.phaseContent}>
                {/* Card View within phase */}
                {viewMode === 'cards' && (
                  <div className={styles.cardGrid}>
                    {Array.from(phaseCatMap.entries()).map(([cat, catItems]) => {
                      const fulfilled = catItems.filter((i) => i.is_fulfilled).length;
                      const total = catItems.length;
                      const pct = total > 0 ? Math.round((fulfilled / total) * 100) : 0;
                      const isComplete = pct === 100;
                      const cardKey = `${phase}:${cat}`;
                      const isCardExpanded = expandedCardKey === cardKey;
                      return (
                        <div key={cat} className={styles.cardAccordion}>
                          <button
                            type="button"
                            className={`${styles.categoryCard} ${isComplete ? styles.cardComplete : ''} ${isCardExpanded ? styles.cardExpanded : ''}`}
                            style={{ backgroundImage: CATEGORY_GRADIENTS[cat] }}
                            onClick={() => handleCardClick(phase, cat)}
                          >
                            {isComplete && (
                              <span className={styles.cardCompleteOverlay}>&#10003;</span>
                            )}
                            <span className={styles.cardIcon}>{CATEGORY_ICONS[cat]}</span>
                            <span className={styles.cardName}>{t(`preflight.categories.${cat}`)}</span>
                            <div className={styles.cardReadiness}>
                              <div className={styles.cardBar}>
                                <div
                                  className={`${styles.cardBarFill} ${isComplete ? styles.cardBarFillComplete : styles.cardBarFillPartial}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className={styles.cardBarText}>
                                {t('preflight.ofTotal', {
                                  fulfilled: String(fulfilled),
                                  total: String(total),
                                })}
                              </span>
                            </div>
                            <svg
                              width="14" height="14" viewBox="0 0 24 24" fill="none"
                              stroke="var(--text-muted)" strokeWidth="2"
                              className={`${styles.cardChevron} ${isCardExpanded ? styles.cardChevronOpen : ''}`}
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>

                          {/* Expanded item rows beneath the card */}
                          {isCardExpanded && (
                            <div className={styles.cardItemList}>
                              {catItems.map((item) => (
                                <ItemRow
                                  key={item.item_guid}
                                  item={item}
                                  allItems={items}
                                  canEdit={canEdit}
                                  t={t}
                                  onToggle={handleToggleFulfilled}
                                  onStateTransition={handleStateTransition}
                                  onEdit={handleEditItem}
                                  onDelete={handleDeleteItem}
                                  onVisibilityChange={handleVisibilityToggle}
                                  expanded={expandedItemGuid === item.item_guid}
                                  onExpand={setExpandedItemGuid}
                                  selectionMode={selectionMode}
                                  isSelected={selectedGuids.has(item.item_guid)}
                                  onToggleSelect={toggleSelectItem}
                                  pendingNote={pendingNoteGuid === item.item_guid}
                                  pendingNoteText={pendingNoteText}
                                  onNoteChange={setPendingNoteText}
                                  onNoteSave={handleSaveFulfillmentNote}
                                  onNoteDismiss={handleDismissFulfillmentNote}
                                  noteInputRef={pendingNoteGuid === item.item_guid ? fulfillmentNoteRef : null}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* List View within phase */}
                {viewMode === 'list' && Array.from(phaseCatMap.entries()).map(([cat, catItems]) => {
                  const collapsed = collapsedCats.has(cat);
                  const fulfilled = catItems.filter((i) => i.is_fulfilled).length;
                  return (
                    <div key={cat} className={styles.categoryGroup}>
                      <div
                        className={styles.categoryHeader}
                        onClick={() => collapsedCats.has(cat) ? expandCategory(cat) : collapseCategory(cat)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') collapsedCats.has(cat) ? expandCategory(cat) : collapseCategory(cat); }}
                      >
                        <span className={styles.categoryIcon}>{CATEGORY_ICONS[cat]}</span>
                        <span className={styles.categoryName}>{t(`preflight.categories.${cat}`)}</span>
                        <span className={styles.categoryCount}>
                          {fulfilled}/{catItems.length}
                        </span>
                        <svg
                          width="14" height="14" viewBox="0 0 24 24" fill="none"
                          stroke="var(--text-muted)" strokeWidth="2"
                          style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </div>

                      {!collapsed && catItems.map((item) => (
                        <ItemRow
                          key={item.item_guid}
                          item={item}
                          allItems={items}
                          canEdit={canEdit}
                          t={t}
                          onToggle={handleToggleFulfilled}
                          onStateTransition={handleStateTransition}
                          onEdit={handleEditItem}
                          onDelete={handleDeleteItem}
                          onVisibilityChange={handleVisibilityToggle}
                          expanded={expandedItemGuid === item.item_guid}
                          onExpand={setExpandedItemGuid}
                          pendingNote={pendingNoteGuid === item.item_guid}
                          pendingNoteText={pendingNoteText}
                          onNoteChange={setPendingNoteText}
                          onNoteSave={handleSaveFulfillmentNote}
                          onNoteDismiss={handleDismissFulfillmentNote}
                          noteInputRef={pendingNoteGuid === item.item_guid ? fulfillmentNoteRef : null}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Template notice ──────────────────────────────────────── */}
      {templateNotice && (
        <div className={styles.templateNotice}>{templateNotice}</div>
      )}

      {/* ── Add / Template buttons ────────────────────────────────── */}
      {canEdit && !editingItem && (
        <div className={styles.actionRow}>
          <button type="button" className={styles.addBtn} onClick={handleAddItem}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t('preflight.addItem')}
          </button>
          <button
            type="button"
            className={styles.templateBtn}
            onClick={() => setShowImportModal(true)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
            {t('preflight.importFromExcel')}
          </button>
        </div>
      )}

      {/* ── Template picker (populated state — hidden, templates only via empty state) */}
      {showTemplatePicker && (
        <TemplatePicker
          templates={templates}
          t={t}
          onApply={handleApplyTemplate}
          onClose={() => setShowTemplatePicker(false)}
        />
      )}

      {/* ── Edit modal ──────────────────────────────────────────── */}
      {editingItem && (
        <EditModal
          item={editingItem}
          allItems={items}
          isNew={isNewItem}
          t={t}
          onChange={setEditingItem}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
          ganttTasks={ganttTasks}
        />
      )}

      {showImportModal && (
        <ResourceImportModal
          projectGuid={projectGuid}
          existingCount={items.length > 0 ? Math.max(...items.map((i) => i.sort_order)) + 1 : 1}
          onImport={handleImport}
          onCancel={() => setShowImportModal(false)}
        />
      )}
    </div>
  );
}

/* ================================================================== */
/*  ItemRow                                                            */
/* ================================================================== */

/* ── State color config for inline badge rendering ─────────── */

const STATE_BADGE_CONFIG: Record<PreflightState, { color: string; bg: string }> = {
  draft:     { color: 'var(--text-muted)',    bg: 'rgba(100,116,139,0.12)' },
  seeking:   { color: 'var(--info-text)',     bg: 'rgba(96,165,250,0.12)' },
  blocked:   { color: 'var(--color-error)',   bg: 'rgba(239,68,68,0.12)' },
  partial:   { color: 'var(--color-warning)', bg: 'rgba(245,158,11,0.12)' },
  met:       { color: 'var(--color-success)', bg: 'rgba(34,197,94,0.12)' },
  withdrawn: { color: 'var(--text-muted)',    bg: 'rgba(100,116,139,0.08)' },
};

interface ItemRowProps {
  item: PreflightItem;
  allItems: PreflightItem[];
  canEdit: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
  onToggle: (item: PreflightItem) => void;
  onStateTransition: (item: PreflightItem, newState: PreflightState) => void;
  onEdit: (item: PreflightItem) => void;
  onDelete: (item: PreflightItem) => void;
  onVisibilityChange: (item: PreflightItem, newVisibility: PreflightVisibility) => void;
  expanded: boolean;
  onExpand: (guid: string | null) => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (guid: string) => void;
  /** DEV-647: Whether the inline fulfillment note input is pending for this item. */
  pendingNote?: boolean;
  pendingNoteText?: string;
  onNoteChange?: (text: string) => void;
  onNoteSave?: (itemGuid: string) => void;
  onNoteDismiss?: () => void;
  noteInputRef?: React.RefObject<HTMLInputElement> | null;
}

function ItemRow({ item, allItems, canEdit, t, onToggle, onStateTransition, onEdit, onDelete, onVisibilityChange, expanded, onExpand, selectionMode, isSelected, onToggleSelect, pendingNote, pendingNoteText, onNoteChange, onNoteSave, onNoteDismiss, noteInputRef }: ItemRowProps) {
  const [showStateMenu, setShowStateMenu] = useState(false);
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);

  /**
   * DEV-86: Derive a fulfillment fingerprint so blocking state recomputes
   * whenever ANY item's is_fulfilled changes.
   */
  const fulfillmentKey = useMemo(
    () => allItems.map(i => `${i.item_guid}:${i.is_fulfilled ? '1' : '0'}`).join('|'),
    [allItems]
  );

  const blockingGuids = useMemo(
    () => getBlockingItems(item, allItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.item_guid, item.depends_on, fulfillmentKey]
  );
  const isBlocked = blockingGuids.length > 0;

  const blockingLabels = useMemo(() => {
    if (!isBlocked) return [];
    const itemMap = new Map(allItems.map(i => [i.item_guid, i]));
    return blockingGuids.map(g => itemMap.get(g)?.label ?? g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlocked, blockingGuids, fulfillmentKey]);

  const isMet = item.state === 'met';
  const cfg = STATE_BADGE_CONFIG[item.state];
  const transitions = STATE_TRANSITIONS[item.state] ?? [];

  return (
    <div className={`${styles.itemCard} ${expanded ? styles.itemCardExpanded : ''}`}>
      {/* Main clickable row */}
      <div
        className={styles.itemRowMain}
        onClick={() => selectionMode ? onToggleSelect?.(item.item_guid) : onExpand(expanded ? null : item.item_guid)}
      >
        {/* Selection checkbox (multi-select mode) */}
        {selectionMode && (
          <label
            className={styles.selectCheckbox}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isSelected ?? false}
              onChange={() => onToggleSelect?.(item.item_guid)}
            />
            <span className={styles.checkboxVisual}>
              {isSelected && <span className={styles.checkboxCheck}>{'\u2713'}</span>}
            </span>
          </label>
        )}

        {/* Toggle button (met/not met) */}
        <button
          type="button"
          className={`${styles.toggleBtn} ${isMet ? styles.toggleBtnMet : ''}`}
          onClick={(e) => { e.stopPropagation(); onToggle(item); }}
          title={isMet ? t('preflight.reopen') : t('preflight.markDone')}
        >
          {isMet ? '\u2713' : ''}
        </button>

        {/* Label + description */}
        <div className={styles.itemContent}>
          <p className={`${styles.itemLabel} ${isMet || item.state === 'withdrawn' ? styles.itemLabelFulfilled : ''}`}>
            {item.label}
          </p>
          {item.description && (
            <p className={styles.itemDescription}>{item.description}</p>
          )}
        </div>

        {/* State badge with dropdown */}
        <span
          className={styles.stateBadgeBtn}
          style={{ '--state-bg': cfg.bg, '--state-color': cfg.color } as React.CSSProperties}
        >
          <span className={styles.stateBadgeDot} />
          {t(`preflight.states.${item.state}`)}
          {canEdit && transitions.length > 0 && (
            <button
              type="button"
              className={styles.stateDropdownTrigger}
              onClick={(e) => { e.stopPropagation(); setShowStateMenu(!showStateMenu); }}
            >
              {'\u25BE'}
            </button>
          )}
          {showStateMenu && (
            <div className={styles.stateDropdownMenu}>
              {transitions.map((s) => {
                const sCfg = STATE_BADGE_CONFIG[s];
                return (
                  <button
                    key={s}
                    type="button"
                    className={styles.stateDropdownItem}
                    style={{ '--item-color': sCfg.color } as React.CSSProperties}
                    onClick={(e) => { e.stopPropagation(); onStateTransition(item, s); setShowStateMenu(false); }}
                  >
                    <span className={styles.stateDropdownDot} />
                    {t(`preflight.states.${s}`)}
                  </button>
                );
              })}
            </div>
          )}
        </span>

        {/* Visibility badge — dropdown when editable */}
        <span
          className={`${styles.visibilityBadge} ${item.visibility === 'public' ? styles.visibilityPublic : styles.visibilityInternal}`}
        >
          {t(`preflight.visibilityOptions.${item.visibility}`)}
          {canEdit && (
            <button
              type="button"
              className={styles.visibilityDropdownTrigger}
              onClick={(e) => { e.stopPropagation(); setShowVisibilityMenu(!showVisibilityMenu); }}
            >
              {'\u25BE'}
            </button>
          )}
          {showVisibilityMenu && (
            <div className={styles.visibilityDropdownMenu}>
              {(['public', 'internal'] as PreflightVisibility[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`${styles.visibilityDropdownItem} ${v === 'public' ? styles.visibilityDropdownPublic : styles.visibilityDropdownInternal} ${item.visibility === v ? styles.visibilityDropdownItemActive : ''}`}
                  onClick={(e) => { e.stopPropagation(); onVisibilityChange(item, v); setShowVisibilityMenu(false); }}
                >
                  {t(`preflight.visibilityOptions.${v}`)}
                  <span className={`${styles.visibilityCheck} ${item.visibility === v ? styles.visibilityCheckVisible : ''}`}>{'✓'}</span>
                </button>
              ))}
            </div>
          )}
        </span>

        {/* Line total (qty × unit cost) */}
        {item.unit_cost != null && item.unit_cost > 0 && (
          <span className={styles.lineCost} title={t('preflight.estimatedTotal')}>
            ${((item.estimated_total != null ? item.estimated_total : (item.target_quantity ?? 1) * item.unit_cost)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        )}

        {/* Schedule link indicator */}
        {item.gantt_task_id && (
          <span className={styles.scheduleLinkIcon} title={t('preflight.linkedToSchedule')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
            </svg>
          </span>
        )}

        {/* Expand chevron */}
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2"
          className={`${styles.expandChevron} ${expanded ? styles.expandChevronOpen : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* ── DEV-647: Fulfillment note inline input (shows after met transition) ── */}
      {pendingNote && isMet && (
        <div className={styles.fulfillmentNoteBar}>
          <div className={styles.fulfillmentNoteInner}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--success-text, #4ade80)" strokeWidth="2" className={styles.fulfillmentNoteIcon}>
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <span className={styles.fulfillmentNoteLabel}>{t('preflight.acquired')}</span>
            <input
              ref={noteInputRef as React.RefObject<HTMLInputElement>}
              type="text"
              className={styles.fulfillmentNoteInput}
              value={pendingNoteText || ''}
              onChange={(e) => onNoteChange?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onNoteSave?.(item.item_guid);
                if (e.key === 'Escape') onNoteDismiss?.();
              }}
              placeholder={t('preflight.fulfillmentNotePlaceholder')}
            />
            <button
              type="button"
              className={styles.fulfillmentNoteSaveBtn}
              onClick={() => onNoteSave?.(item.item_guid)}
            >
              {t('preflight.saveItem')}
            </button>
            <button
              type="button"
              className={styles.fulfillmentNoteSkipBtn}
              onClick={() => onNoteDismiss?.()}
            >
              {t('preflight.fulfillmentNoteSkip')}
            </button>
          </div>
        </div>
      )}

      {/* ── DEV-647: Existing fulfillment note display ── */}
      {item.fulfillment_note && !pendingNote && (
        <div className={styles.fulfillmentNoteDisplay}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted, #64748b)" strokeWidth="2" className={styles.fulfillmentNoteIcon}>
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          <p className={styles.fulfillmentNoteText}>{item.fulfillment_note}</p>
        </div>
      )}

      {/* Expanded detail panel */}
      {expanded && (
        <div className={styles.detailPanel}>
          <div className={styles.detailGrid}>
            <div>
              <div className={styles.detailGridLabel}>{t('preflight.priorityLabel')}</div>
              <div className={styles.detailGridValue}>{t(`preflight.priorities.${item.priority}`)}</div>
            </div>
            <div>
              <div className={styles.detailGridLabel}>{t('preflight.category')}</div>
              <div className={styles.detailGridValue}>{t(`preflight.categories.${item.category}`)}</div>
            </div>
            <div>
              <div className={styles.detailGridLabel}>{t('preflight.phase')}</div>
              <div className={styles.detailGridValue}>{t(`preflight.phases.${item.phase ?? 'preparation'}`)}</div>
            </div>
          </div>

          {/* Progress for threshold items */}
          {item.requirement_type === 'threshold' && item.target_quantity != null && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className={styles.detailGridLabel}>{t('preflight.currentQuantity')}</span>
              <span className={styles.detailGridValue}>
                {t('preflight.progressFormat', {
                  current: String(item.current_quantity),
                  target: String(item.target_quantity),
                  unit: item.unit || '',
                })}
              </span>
            </div>
          )}

          {/* Descriptor tags */}
          {item.descriptor_tags?.length > 0 && (
            <div className={styles.detailTags}>
              {item.descriptor_tags.map((tag) => (
                <span key={tag} className={styles.detailTag}>{tag}</span>
              ))}
            </div>
          )}

          {/* Mission link */}
          {item.mission_subkpi_key && (
            <div style={{ marginTop: 8 }}>
              <span className={styles.detailGridLabel}>{t('preflight.missionLink')}</span>
              <span className={styles.missionLinkBadge} style={{ marginInlineStart: 8 }}>
                {item.mission_subkpi_key}
              </span>
            </div>
          )}

          {/* Blocked-by list */}
          {isBlocked && item.state !== 'met' && (
            <p className={styles.detailBlockedList}>
              {t('preflight.blockedBy')}: {blockingLabels.join(', ')}
            </p>
          )}

          {/* Action buttons */}
          {canEdit && (
            <div className={styles.detailActions}>
              <button
                type="button"
                className={styles.detailEditBtn}
                onClick={(e) => { e.stopPropagation(); onEdit(item); }}
              >
                {t('preflight.editItem')}
              </button>
              <button
                type="button"
                className={styles.detailDeleteBtn}
                onClick={(e) => { e.stopPropagation(); onDelete(item); }}
              >
                {t('preflight.deleteItem')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  EditForm                                                           */
/* ================================================================== */

interface EditFormProps {
  item: PreflightItem;
  allItems: PreflightItem[];
  isNew: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
  onChange: (item: PreflightItem) => void;
  onSave: () => void;
  onCancel: () => void;
  ganttTasks: GanttTask[];
}

/* ================================================================== */
/*  TemplatePicker                                                     */
/* ================================================================== */

interface TemplatePickerProps {
  templates: PreflightTemplate[];
  t: (key: string, opts?: Record<string, unknown>) => string;
  onApply: (templateId: string) => void;
  onClose: () => void;
}

function TemplatePicker({ templates, t, onApply, onClose }: TemplatePickerProps) {
  if (templates.length === 0) {
    return (
      <div className={styles.templatePicker}>
        <p className={styles.templateEmpty}>{t('preflight.noTemplates')}</p>
      </div>
    );
  }

  return (
    <div className={styles.templatePicker}>
      <div className={styles.templatePickerHeader}>
        <span className={styles.templatePickerTitle}>{t('preflight.chooseTemplate')}</span>
        <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div className={styles.templateList}>
        {templates.map((tp) => (
          <button
            key={tp.template_guid}
            type="button"
            className={styles.templateCard}
            onClick={() => onApply(tp.template_guid)}
          >
            <span className={styles.templateCardName}>{tp.name}</span>
            {tp.description && (
              <span className={styles.templateCardDesc}>{tp.description}</span>
            )}
            <span className={styles.templateCardMeta}>
              {tp.items.length} {tp.items.length === 1 ? 'item' : 'items'}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  EditModal                                                          */
/* ================================================================== */

function EditModal({ item, allItems, isNew, t, onChange, onSave, onCancel, ganttTasks }: EditFormProps) {
  const update = (patch: Partial<PreflightItem>) => onChange({ ...item, ...patch });
  const panelRef = useRef<HTMLDivElement>(null);

  /** Items eligible as dependencies: same project, not self (case-insensitive). */
  const eligibleDeps = useMemo(
    () => {
      const selfGuid = item.item_guid.toUpperCase();
      return allItems.filter(i => i.item_guid.toUpperCase() !== selfGuid);
    },
    [allItems, item.item_guid]
  );

  /** Current depends_on set for quick lookup (case-insensitive). */
  const currentDeps = useMemo(() => {
    return new Set((item.depends_on ?? []).map(g => g.toUpperCase()));
  }, [item.depends_on]);

  /** Detect circular dependency warning. */
  const circularWarning = useMemo(() => {
    if (!item.depends_on?.length) return false;
    return hasCircularDependency(item.item_guid, item.depends_on, allItems);
  }, [item.item_guid, item.depends_on, allItems]);

  /** Dependency search filter. */
  const [depSearch, setDepSearch] = useState('');
  const filteredEligibleDeps = useMemo(() => {
    const available = eligibleDeps.filter(i => !currentDeps.has(i.item_guid.toUpperCase()));
    if (!depSearch.trim()) return available;
    const q = depSearch.toLowerCase();
    return available.filter(i =>
      i.label.toLowerCase().includes(q) ||
      (i.description ?? '').toLowerCase().includes(q) ||
      i.category.toLowerCase().includes(q)
    );
  }, [eligibleDeps, currentDeps, depSearch]);

  const handleAddDep = useCallback((depGuid: string) => {
    const newDeps = [...(item.depends_on ?? []), depGuid];
    update({ depends_on: newDeps });
    setDepSearch('');
  }, [item.depends_on]);

  const handleRemoveDep = useCallback((depGuid: string) => {
    const upper = depGuid.toUpperCase();
    const newDeps = (item.depends_on ?? []).filter(g => g.toUpperCase() !== upper);
    update({ depends_on: newDeps });
  }, [item.depends_on]);

  /* Close on Escape */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onCancel]);

  /* Close on backdrop click */
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onCancel();
      }
    },
    [onCancel]
  );

  // Computed estimated total for the cost summary
  const estimatedTotal =
    item.target_quantity != null && item.unit_cost != null
      ? item.target_quantity * item.unit_cost
      : 0;

  // Keep category and cost_category in sync (unified taxonomy)
  const handleCategoryChange = (val: string) => {
    const cat = normalizeCategory(val);
    update({ category: cat, cost_category: cat });
  };

  return ReactDOM.createPortal(
    <div className={styles.modalOverlay} onClick={handleBackdropClick} role="dialog" aria-modal="true">
      <div className={styles.modalPanel} ref={panelRef} style={{ width: 'min(680px, 94vw)' }}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
            {isNew ? t('preflight.addItem') : t('preflight.editItem')}
          </h3>
          <button type="button" className={styles.modalCloseBtn} onClick={onCancel} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* Full-width: Resource Name */}
          <div className={styles.editField}>
            <label className={styles.editLabel}>{t('preflight.resourceName')}</label>
            <input
              className={styles.editInput}
              type="text"
              value={item.label}
              onChange={(e) => update({ label: e.target.value })}
              placeholder={t('preflight.labelPlaceholder')}
              style={{ fontSize: '0.9375rem', fontWeight: 600 }}
              autoFocus
            />
          </div>

          {/* Full-width: Description (below name, above two-column grid) */}
          <div className={styles.editField}>
            <label className={styles.editLabel}>{t('preflight.description')}</label>
            <textarea
              className={styles.editInput}
              value={item.description || ''}
              onChange={(e) => update({ description: e.target.value })}
              placeholder={t('preflight.descriptionPlaceholder')}
              rows={2}
              style={{ resize: 'vertical', minHeight: '48px' }}
            />
          </div>

          {/* ── Two-column layout (matches Option B mockup) ── */}
          <div className={styles.editTwoCol}>
            {/* ── LEFT COLUMN: Resource Details ── */}
            <div className={styles.editColSection}>
              <div className={styles.editColTitle}>{t('preflight.resourceDetails')}</div>

              {/* Category */}
              <div className={styles.editField}>
                <label className={styles.editLabel}>{t('preflight.category')}</label>
                <select
                  className={styles.editSelect}
                  value={item.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                >
                  {RESOURCE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{t(`preflight.categories.${cat}`)}</option>
                  ))}
                </select>
                <span className={styles.editHint}>{t(`preflight.categoryHints.${item.category}`)}</span>
              </div>

              {/* Phase */}
              <div className={styles.editField}>
                <label className={styles.editLabel}>{t('preflight.phase')}</label>
                <select
                  className={styles.editSelect}
                  value={item.phase ?? 'preparation'}
                  onChange={(e) => update({ phase: e.target.value as ResourcePhase })}
                >
                  {RESOURCE_PHASES.map((p) => (
                    <option key={p} value={p}>{t(`preflight.phases.${p}`)}</option>
                  ))}
                </select>
              </div>

              {/* State + Priority (side by side within left column) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div className={styles.editField}>
                  <label className={styles.editLabel}>{t('preflight.stateLabel')}</label>
                  <select
                    className={styles.editSelect}
                    value={item.state ?? 'seeking'}
                    onChange={(e) => {
                      const newState = e.target.value as PreflightState;
                      update({
                        state: newState,
                        is_fulfilled: newState === 'met',
                        withdrawn_at: newState === 'withdrawn' ? new Date().toISOString() : null,
                      });
                    }}
                  >
                    {PREFLIGHT_STATES.map((s) => (
                      <option key={s} value={s}>{t(`preflight.states.${s}`)}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.editField}>
                  <label className={styles.editLabel}>{t('preflight.priorityLabel')}</label>
                  <select
                    className={styles.editSelect}
                    value={item.priority ?? 'important'}
                    onChange={(e) => update({ priority: e.target.value as PreflightPriority })}
                  >
                    {PREFLIGHT_PRIORITIES.map((p) => (
                      <option key={p} value={p}>{t(`preflight.priorities.${p}`)}</option>
                    ))}
                  </select>
                </div>
              </div>

            </div>

            {/* ── RIGHT COLUMN: Cost & Tracking ── */}
            <div className={styles.editColSection}>
              <div className={styles.editColTitle}>{t('preflight.costTracking')}</div>

              {/* Quantity + Unit (side by side within right column) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div className={styles.editField}>
                  <label className={styles.editLabel}>{t('preflight.targetQuantity')}</label>
                  <input
                    className={styles.editInput}
                    type="number"
                    min="0"
                    step="1"
                    value={item.target_quantity ?? ''}
                    onChange={(e) => {
                      const val = e.target.value ? Number(e.target.value) : null;
                      update({ target_quantity: val });
                    }}
                    placeholder="1"
                    style={{ textAlign: 'end' }}
                  />
                </div>
                <div className={styles.editField}>
                  <label className={styles.editLabel}>{t('preflight.unit')}</label>
                  <input
                    className={styles.editInput}
                    type="text"
                    value={item.unit || ''}
                    onChange={(e) => update({ unit: e.target.value })}
                    placeholder={t('preflight.unitPlaceholder')}
                  />
                </div>
              </div>

              {/* Unit Cost */}
              <div className={styles.editField}>
                <label className={styles.editLabel}>{t('preflight.unitCost')}</label>
                <input
                  className={styles.editInput}
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.unit_cost ?? ''}
                  onChange={(e) => update({ unit_cost: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="0.00"
                  style={{ textAlign: 'end' }}
                />
              </div>

              {/* Estimated Total */}
              <div
                style={{
                  background: estimatedTotal > 0 ? 'rgba(34, 197, 94, 0.08)' : 'var(--bg-primary)',
                  border: `1px solid ${estimatedTotal > 0 ? 'rgba(34, 197, 94, 0.2)' : 'var(--border-default)'}`,
                  borderRadius: '6px',
                  padding: '0.625rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: estimatedTotal > 0 ? 'var(--color-success)' : 'var(--text-muted)' }}>
                  {t('preflight.estimatedTotal')}
                </span>
                <span style={{ fontSize: '1.125rem', fontWeight: 700, color: estimatedTotal > 0 ? 'var(--color-success)' : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                  ${estimatedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>

              {/* Actual Cost + % Complete (side by side within right column) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div className={styles.editField}>
                  <label className={styles.editLabel}>{t('preflight.actualCost')}</label>
                  <input
                    className={styles.editInput}
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.actual_cost ?? ''}
                    onChange={(e) => update({ actual_cost: e.target.value ? parseFloat(e.target.value) : null })}
                    placeholder="0.00"
                    style={{ textAlign: 'end' }}
                  />
                </div>
                <div className={styles.editField}>
                  <label className={styles.editLabel}>{t('preflight.percentComplete')}</label>
                  {item.gantt_task_id ? (
                    <span className={styles.editHint} style={{ paddingBlockStart: '0.375rem' }}>
                      {t('preflight.percentCompleteLinked')}
                    </span>
                  ) : (
                    <input
                      className={styles.editInput}
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={item.percent_complete ?? 0}
                      onChange={(e) => update({ percent_complete: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
                      style={{ textAlign: 'end' }}
                    />
                  )}
                </div>
              </div>

              {/* Schedule Link */}
              <div className={styles.editField}>
                <label className={styles.editLabel} title={t('preflight.scheduleLinkHelp')}>
                  {t('preflight.scheduleLink')}
                </label>
                <select
                  className={styles.editSelect}
                  value={item.gantt_task_id || ''}
                  onChange={(e) => update({ gantt_task_id: e.target.value || null })}
                >
                  <option value="">{t('preflight.scheduleLinkNone')}</option>
                  {ganttTasks
                    .filter((gt) => !gt.isSummary)
                    .map((gt) => (
                      <option key={gt.id} value={gt.id}>
                        {gt.wbs ? `${gt.wbs} — ` : `#${gt.taskId} — `}{gt.taskName}
                      </option>
                    ))}
                </select>
              </div>

            </div>
          </div>

          {/* ── Full-width bottom section: Advanced options ── */}
          <details className={styles.editAdvanced}>
            <summary className={styles.editAdvancedSummary}>{t('preflight.advancedOptions')}</summary>
            <div className={styles.editAdvancedBody}>
              {/* Requirement Type + Visibility */}
              <div className={styles.editRow}>
                <div className={styles.editFieldSmall}>
                  <label className={styles.editLabel}>{t('preflight.requirementType')}</label>
                  <select
                    className={styles.editSelect}
                    value={item.requirement_type}
                    onChange={(e) => {
                      const rt = e.target.value as PreflightRequirementType;
                      update({
                        requirement_type: rt,
                        target_quantity: rt === 'threshold' ? (item.target_quantity ?? 1) : null,
                        unit: rt === 'threshold' ? (item.unit ?? '') : null,
                      });
                    }}
                  >
                    {PREFLIGHT_REQUIREMENT_TYPES.map((rt) => (
                      <option key={rt} value={rt}>{t(`preflight.requirementTypes.${rt}`)}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.editFieldSmall}>
                  <label className={styles.editLabel}>{t('preflight.visibility')}</label>
                  <select
                    className={styles.editSelect}
                    value={item.visibility}
                    onChange={(e) => update({ visibility: e.target.value as PreflightVisibility })}
                  >
                    <option value="internal">{t('preflight.visibilityOptions.internal')}</option>
                    <option value="public">{t('preflight.visibilityOptions.public')}</option>
                  </select>
                </div>
              </div>

              {/* Threshold current quantity */}
              {item.requirement_type === 'threshold' && (
                <div className={styles.editRow}>
                  <div className={styles.editFieldSmall}>
                    <label className={styles.editLabel}>{t('preflight.currentQuantity')}</label>
                    <input
                      className={styles.editInput}
                      type="number"
                      min="0"
                      value={item.current_quantity}
                      onChange={(e) => {
                        const val = Number(e.target.value) || 0;
                        const isMet = item.target_quantity != null ? val >= item.target_quantity : false;
                        update({
                          current_quantity: val,
                          is_fulfilled: isMet,
                          state: isMet ? 'met' : (val > 0 ? 'partial' : (item.state === 'met' ? 'seeking' : item.state)),
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Cost Note */}
              <div className={styles.editField}>
                <label className={styles.editLabel}>{t('preflight.costNote')}</label>
                <input
                  className={styles.editInput}
                  type="text"
                  value={item.cost_note ?? ''}
                  onChange={(e) => update({ cost_note: e.target.value || null })}
                  placeholder={t('preflight.costNotePlaceholder')}
                />
              </div>

              {/* Descriptor Tags */}
              <div className={styles.editField}>
                <label className={styles.editLabel}>{t('preflight.descriptorTagsLabel')}</label>
                <div className={styles.tagChips}>
                  {(item.descriptor_tags ?? []).map((tag) => (
                    <TagChip
                      key={tag}
                      tag={tag}
                      onRemove={() =>
                        update({ descriptor_tags: (item.descriptor_tags ?? []).filter((t) => t !== tag) })
                      }
                    />
                  ))}
                </div>
                <select
                  className={styles.editSelect}
                  value=""
                  onChange={(e) => {
                    const tag = e.target.value as DescriptorTag;
                    if (tag && !(item.descriptor_tags ?? []).includes(tag)) {
                      update({ descriptor_tags: [...(item.descriptor_tags ?? []), tag] });
                    }
                  }}
                >
                  <option value="">— {t('preflight.addTag')} —</option>
                  {DESCRIPTOR_TAGS
                    .filter((tag) => !(item.descriptor_tags ?? []).includes(tag))
                    .map((tag) => (
                      <option key={tag} value={tag}>{t(`preflight.descriptorTags.${tag}`)}</option>
                    ))}
                </select>
              </div>

              {/* Dependencies */}
              {!isNew && eligibleDeps.length > 0 && (
                <div className={styles.editField}>
                  <label className={styles.editLabel}>{t('preflight.dependsOn')}</label>
                  <div className={styles.depPickerContainer}>
                    {currentDeps.size > 0 && (
                      <div className={styles.depChipList}>
                        {Array.from(currentDeps).map(depGuidUpper => {
                          const depItem = eligibleDeps.find(
                            i => i.item_guid.toUpperCase() === depGuidUpper
                          );
                          const originalGuid = depItem?.item_guid ?? depGuidUpper;
                          return (
                            <button
                              key={depGuidUpper}
                              type="button"
                              className={styles.depChip}
                              onClick={() => handleRemoveDep(originalGuid)}
                              title={t('preflight.removeDep')}
                            >
                              {depItem?.label ?? depGuidUpper.slice(0, 8)}
                              <span>&times;</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <input
                      className={styles.editInput}
                      type="text"
                      value={depSearch}
                      onChange={(e) => setDepSearch(e.target.value)}
                      placeholder={t('preflight.searchDeps')}
                    />
                    {filteredEligibleDeps.length > 0 && (
                      <select
                        className={styles.editSelect}
                        value=""
                        onChange={(e) => {
                          if (e.target.value) handleAddDep(e.target.value);
                        }}
                      >
                        <option value="">{t('preflight.addDependency')}</option>
                        {filteredEligibleDeps.map(i => (
                          <option key={i.item_guid} value={i.item_guid}>
                            {i.label} ({t(`preflight.categories.${i.category}`)})
                          </option>
                        ))}
                      </select>
                    )}
                    {circularWarning && (
                      <p className={styles.depWarning}>{t('preflight.circularWarning')}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </details>
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnSecondary} onClick={onCancel}>
            {t('preflight.cancelEdit')}
          </button>
          <button type="button" className={styles.btnPrimary} onClick={onSave} disabled={!item.label.trim() || circularWarning}>
            {t('preflight.saveItem')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
